import React, { useEffect, useMemo, useState } from 'react';
import type { Client, Language, Order, OrderItem, Product } from '../types';
import type { Dict } from '../shared/i18n';
import { LOCALES } from '../shared/i18n';
import { lineSubtotal, localDateString, orderTotal, round2, todayKey } from '../shared/domain';
import { canAdd } from '../shared/entitlements';
import type { PlanLimits } from '../shared/config';
import { Card, LimitNotice } from '../components/shared';

interface CommandPageProps {
  products: Product[];
  clients: Client[];
  lang: Language;
  t: Dict;
  limits: PlanLimits;
  ordersThisMonth: number;
  editingOrder: Order | null;
  onSaveOrder: (order: Order) => void;
  onCancel: () => void;
}

const CommandPage: React.FC<CommandPageProps> = ({
  products, clients, lang, t, limits, ordersThisMonth, editingOrder, onSaveOrder, onCancel,
}) => {
  const [clientId, setClientId] = useState(editingOrder?.clientId ?? '');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [showAll, setShowAll] = useState(!!editingOrder);
  const [paidInput, setPaidInput] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!editingOrder) return;
    setQuantities(Object.fromEntries(editingOrder.items.map((i) => [i.productId, i.quantity])));
    setClientId(editingOrder.clientId === 'anonymous' ? '' : editingOrder.clientId);
    setNote(editingOrder.note ?? '');
    if (editingOrder.paidAmount !== editingOrder.totalAmount) setPaidInput(String(editingOrder.paidAmount));
    setShowAll(true);
  }, [editingOrder]);

  const key = todayKey();
  const visibleClients = useMemo(
    () => (showAll ? clients : clients.filter((c) => c.deliveryDays.includes(key))),
    [clients, showAll, key],
  );

  const items = useMemo<OrderItem[]>(
    () => products
      .filter((p) => (quantities[p.id] ?? 0) > 0)
      .map((p) => ({
        productId: p.id,
        productName: p.name,
        quantity: quantities[p.id],
        unitPrice: p.price,
        subtotal: lineSubtotal(quantities[p.id], p.price),
      })),
    [products, quantities],
  );

  const total = useMemo(() => orderTotal(items), [items]);
  const nf = new Intl.NumberFormat(lang === 'ar' ? 'ar-MA-u-nu-latn' : LOCALES[lang], {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  // Editing is never blocked by the monthly cap: a user at their limit must
  // still be able to correct yesterday's order.
  const gate = editingOrder ? { allowed: true } : canAdd(limits, 'ordersThisMonth', ordersThisMonth);

  const bump = (id: string, delta: number) => {
    setQuantities((prev) => {
      const next = Math.max(0, (prev[id] ?? 0) + delta);
      if (next === 0) {
        const { [id]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: next };
    });
  };

  const handleSave = () => {
    if (total <= 0 || !gate.allowed) return;
    const client = clients.find((c) => c.id === clientId);
    const parsedPaid = paidInput.trim() === '' ? total : round2(Number(paidInput.replace(',', '.')));
    const paidAmount = Number.isFinite(parsedPaid) && parsedPaid >= 0 ? Math.min(parsedPaid, total) : total;

    onSaveOrder({
      id: editingOrder?.id ?? crypto.randomUUID(),
      clientId: client?.id ?? 'anonymous',
      clientName: client?.name ?? t.passingClient,
      items,
      totalAmount: total,
      paidAmount,
      timestamp: editingOrder?.timestamp ?? Date.now(),
      dateString: editingOrder?.dateString ?? localDateString(),
      note: note.trim() || undefined,
    });
  };

  if (products.length === 0) {
    return (
      <div className="text-center py-16 space-y-3">
        <span className="text-4xl opacity-30" aria-hidden="true">📦</span>
        <p className="text-sm font-extrabold text-slate-400">{t.addProduct}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-56 qd-enter">
      {!gate.allowed && <LimitNotice what="ordersThisMonth" t={t} onUpgrade={onCancel} />}

      <Card className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.selectClient}</span>
          {!editingOrder && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className={`text-[10px] font-extrabold px-3 py-1.5 rounded-full ${
                showAll ? 'bg-[#C1272D] text-white' : 'bg-slate-100 text-slate-500'
              }`}
            >
              {showAll ? t.todayClients : t.allClients}
            </button>
          )}
        </div>

        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          aria-label={t.selectClient}
          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 font-bold min-h-[48px]"
        >
          <option value="">— {t.passingClient} —</option>
          {visibleClients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}{c.sector ? ` (${c.sector})` : ''}</option>
          ))}
        </select>

        {visibleClients.length === 0 && !showAll && (
          <p className="text-[11px] text-[#C1272D] font-bold">{t.noClientToday}</p>
        )}
      </Card>

      <div className="space-y-2">
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">{t.products}</p>
        {products.map((product) => {
          const qty = quantities[product.id] ?? 0;
          return (
            <Card
              key={product.id}
              className={`flex items-center justify-between gap-3 ${qty > 0 ? 'ring-1 ring-[#C1272D] bg-red-50/40' : ''}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-xl bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center">
                  {product.image
                    ? <img src={product.image} alt="" className="w-full h-full object-cover" />
                    : <span className="text-lg" aria-hidden="true">📦</span>}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 text-sm truncate">{product.name}</p>
                  <p className="text-xs font-extrabold text-[#006233]">
                    {nf.format(product.price)} {t.currency}
                  </p>
                  {qty > 0 && (
                    <p className="text-[10px] text-slate-400 font-bold">
                      {qty} × {nf.format(product.price)} = {nf.format(lineSubtotal(qty, product.price))}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => bump(product.id, -1)}
                  aria-label={`− ${product.name}`}
                  className="w-11 h-11 rounded-xl bg-white border border-slate-200 font-extrabold text-xl"
                >−</button>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={qty}
                  aria-label={product.name}
                  onChange={(e) => {
                    const v = Math.max(0, Math.floor(Number(e.target.value) || 0));
                    setQuantities((prev) => {
                      if (v === 0) { const { [product.id]: _d, ...rest } = prev; return rest; }
                      return { ...prev, [product.id]: v };
                    });
                  }}
                  className="w-12 h-11 text-center font-extrabold bg-transparent border-none outline-none"
                />
                <button
                  onClick={() => bump(product.id, 1)}
                  aria-label={`+ ${product.name}`}
                  className="w-11 h-11 rounded-xl bg-[#C1272D] text-white font-extrabold text-xl"
                >+</button>
              </div>
            </Card>
          );
        })}
      </div>

      {total > 0 && (
        <Card className="space-y-3">
          <label className="block">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.amountPaid}</span>
            <input
              type="text"
              inputMode="decimal"
              value={paidInput}
              onChange={(e) => setPaidInput(e.target.value)}
              placeholder={nf.format(total)}
              className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl p-3 font-bold min-h-[48px]"
            />
          </label>
          <p className="text-[11px] text-slate-400 font-bold">
            {paidInput.trim() === '' ? t.fullyPaid : t.partialPayment}
          </p>
          <label className="block">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.note}</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl p-3 font-bold min-h-[48px]"
            />
          </label>
        </Card>
      )}

      <div className="fixed bottom-16 left-0 right-0 max-w-lg mx-auto px-4 py-4 bg-white/95 backdrop-blur border-t border-slate-200 rounded-t-3xl z-30">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">{t.instantTotal}</span>
          <span className="text-2xl font-extrabold text-[#C1272D]">
            {nf.format(total)} <span className="text-xs">{t.currency}</span>
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 bg-slate-100 text-slate-600 font-extrabold py-4 rounded-xl">
            {t.cancel}
          </button>
          <button
            onClick={handleSave}
            disabled={total <= 0 || !gate.allowed}
            className="flex-[2] bg-[#006233] text-white font-extrabold py-4 rounded-xl disabled:opacity-30"
          >
            {editingOrder ? t.saveChanges : t.saveOrder}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommandPage;
